import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import ReconnectOptions from './ReconnectOptions';
import HandlerConfig from './HandlerConfig';
import EventHandler from 'generic/EventHandler';
import IEventSubscription from 'generic/IEventSubscription';
import { delay } from 'utils/Global';
import HubUnauthorizedError from './HubUnauthorizedError';
import { refreshToken } from 'utils/RockFetch';
import { ResponseType } from 'business/models/Models';

export default class RMHub {
  manualStop: boolean = false;
  private readonly onReconnecting = new EventHandler<Error>();
  private readonly onReconnected = new EventHandler<string>();
  private readonly onConnected = new EventHandler<string>();
  private readonly onUnauthorized = new EventHandler<Error>();

  private hub: HubConnection;
  private hubBuilder: HubConnectionBuilder;

  constructor(
    private readonly name: string,
    private url: string,
    token: string,
    private defaultOptions: ReconnectOptions,
  ) {
    this.hubBuilder = new HubConnectionBuilder().withUrl(url, {
      accessTokenFactory: () => {
        return token;
      },
      withCredentials: true,
    } as any);
    if (defaultOptions.automaticReconnect === true) {
      this.hubBuilder = this.hubBuilder.withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          const retries = retryContext.previousRetryCount;
          return Math.min(
            defaultOptions.baseDelayMs * Math.pow(2, retries),
            defaultOptions.maxDelayMs,
          );
        },
      });
    }
    this.hub = this.hubBuilder.build();
  }

  public get reconnecting() {
    return this.onReconnecting.source;
  }
  public get reconnected() {
    return this.onReconnected.source;
  }
  public get connected() {
    return this.onConnected.source;
  }
  public get unauthorized() {
    return this.onUnauthorized.source;
  }

  public stop() {
    return this.hub.stop();
  }

  private setupEventHandlers(
    handlers: HandlerConfig[] = [],
    options: ReconnectOptions = {},
  ) {
    // reconnecting
    this.hub.onreconnecting((err) => {
      console.info(
        'Connection [',
        this.name,
        '] lost due to error. Reconnecting...',
        err,
      );
      this.onReconnecting.invoke(err);
    });

    // reconnected
    this.hub.onreconnected((connectionId) => {
      console.info(
        'Connection [',
        this.name,
        '] reestablished. ConnectionId:',
        connectionId,
      );
      this.onReconnected.invoke(connectionId);
    });

    // onclose
    this.hub.onclose(async (err) => {
      console.warn('Connection [', this.name, '] closed, attempting to reconnect...', {
        err,
      });
      if (this.manualStop) {
        console.warn(
          'Skip reconnect, connection [',
          this.name,
          '] is stopped intentionally',
        );
        return;
      }

      this.onReconnecting.invoke(
        new Error(`Connection [${this.name}] closed`, { cause: err }),
      );
      try {
        await this.setupConnection(handlers, options);
        this.onReconnected.invoke(this.name);
      } catch (error) {
        if (error instanceof HubUnauthorizedError) {
          this.onUnauthorized.invoke(error);
        }
        console.error('Reconnection [', this.name, '] failed:', error);
      }
    });
  }

  private async createHubWithNewToken() {
    const resultTokens = await refreshToken(false);
    if (resultTokens?.responseType !== ResponseType.Success) {
      throw new Error('Failed to refresh token. Invalid response');
    }
    this.hubBuilder = this.hubBuilder.withUrl(this.url, {
      accessTokenFactory: () => {
        return resultTokens.jwtToken;
      },
      withCredentials: true,
    } as any);
    this.hub = this.hubBuilder.build();
  }

  public async setupConnection(
    handlers: HandlerConfig[] = [],
    options: ReconnectOptions = {},
  ) {
    const attemptSetup = async (attemptNum: number) => {
      console.info(`Setting up connection [${this.name}], attempt ${attemptNum}`);
      try {
        await this.setupConnectionInternal(handlers, options);
        console.info(
          'Connection setup successful [',
          this.name,
          '] on attempt',
          attemptNum,
        );
        return true; // success
      } catch (e) {
        console.error(
          `Connection setup failed [${this.name}] on attempt ${attemptNum}:`,
          e,
        );
        if (e instanceof HubUnauthorizedError) {
          return false;
        } // unauthorized
        throw e; // rethrow unknown errors
      }
    };

    // First attempt
    const firstOk = await attemptSetup(1);
    if (firstOk) {
      return;
    }

    // Unauthorized: refresh token and retry
    await this.createHubWithNewToken();
    const secondOk = await attemptSetup(2);
    if (secondOk) {
      return;
    }

    // Still unauthorized after retry → fail
    console.error('Hub connection unauthorized', this.url);
    this.onUnauthorized.invoke(new HubUnauthorizedError());
  }

  private async setupConnectionInternal(
    handlers: HandlerConfig[] = [],
    options: ReconnectOptions = {},
  ): Promise<any> {
    const mergedOptions = { ...this.defaultOptions, ...options };

    this.setupEventHandlers(handlers, mergedOptions);

    if (this.hub.state !== HubConnectionState.Connected) {
      await this.tryReconnect(handlers, mergedOptions);
    }
    this.onConnected.invoke(this.hub.connectionId);
    console.info('Connection [', this.name, '] established');
  }
  private async tryReconnect(
    handlers: HandlerConfig[],
    options: ReconnectOptions,
  ): Promise<void> {
    let retries = 0;
    const { maxRetries, baseDelayMs, maxDelayMs } = options;

    while (retries < maxRetries) {
      try {
        console.info(
          `Attempting to reconnect [${this.name}], try #${retries + 1} of ${maxRetries}`,
        );
        await this.hub.start();
        this.attachHandlers(handlers);
        return;
      } catch (err) {
        if (this.isUnauthorizedError(err)) {
          console.warn('Hub request unauthorized');
          throw new HubUnauthorizedError();
        }
        // just a fallback if for some reason auto recoonect fails.
        console.error(`Reconnection [${this.name}] attempt ${retries + 1} failed:`, err);
        retries++;

        if (retries >= maxRetries) {
          throw err;
        }

        const delayMs = Math.min(baseDelayMs * 2 ** retries, maxDelayMs);
        await delay(delayMs);
      }
    }
  }
  private isUnauthorizedError(error: any) {
    if (!error) {
      return false;
    }
    const msg = error.toString().toLowerCase();
    return msg.includes('unauthorized') || msg.includes('401');
  }
  public attachHandlers(handlers: HandlerConfig[]): IEventSubscription {
    const handles = handlers.map((handler) =>
      this.attachHandler(handler.eventName, handler.callback),
    );
    return {
      dispose() {
        handles.forEach((handle) => handle.dispose());
      },
    };
  }

  public attachHandler(
    eventName: string,
    callback: (...args: any[]) => void,
  ): IEventSubscription {
    try {
      this.hub.on(eventName, callback);

      return {
        dispose: () => {
          try {
            this.hub.off(eventName, callback);
          } catch (error) {
            console.error(`Error removing handler for ${eventName}:`, error);
          }
        },
      };
    } catch (attachError) {
      console.error(`Failed to attach handler ${eventName}:`, attachError);
      return {
        dispose: () => {
          /* no-op on failed attach */
        },
      };
    }
  }

  public on(path: string, callback: (...args: any[]) => void) {
    this.hub.on(path, callback);
  }

  public off(path: string): void;
  public off(path: string, callback: (...args: any[]) => void): void;
  public off(path: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      this.hub.off(path, callback);
    } else {
      this.hub.off(path);
    }
  }

  public invoke<T>(methodName: string, ...params: any[]) {
    return this.hub.invoke<T>(methodName, ...params);
  }

  public stream<T>(methodName: string, ...params: any[]) {
    return this.hub.stream<T>(methodName, ...params);
  }
}
